import lodashGet from "lodash/get";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  PillarTableColumnArrayFind,
  resolvePillarTableArrayFindHeading,
} from "shared/components/pillar-table/columns/array/PillarTableColumnArrayFind";
import { findPillarTableColumnComponent } from "shared/components/pillar-table/columns/utils/findPillarTableColumnComponent";
import {
  type CustomReportColumnConfig,
  renderCustomReportColumn,
} from "shared/components/pillar-table/custom-reports/customReportColumnsMapper";
import {
  type ExportHtmlToText,
  isPlainExportText,
} from "shared/components/pillar-table/export/export-html-to-text";
import PillarTableExportToExcelColumn, {
  type PillarTableExportToExcelColumnProps,
} from "shared/components/pillar-table/export/PillarTableExportToExcelColumn";
import { pivotPillarTableNestedRows } from "shared/components/pillar-table/export/pivotPillarTableNestedRows";
import { isPillarTableHeadingTemplate } from "shared/components/pillar-table/helpers/renderPillarTableHeading";
import {
  getPillarTableNestedRows,
  isPillarTableNestedTable,
  type PillarTableNestedTableElement,
} from "shared/components/pillar-table/nested/pillarTableNestedTableHelpers";
import {
  PillarTableContext,
  type PillarTableContextType,
} from "shared/components/pillar-table/PillarTableContext";
import { PillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import {
  CustomSessionContext,
  type CustomSessionType,
} from "shared/context/CustomSessionContext";

/** The columns a table exports: `PillarTable.Column`s, nested tables and legacy export columns. */
export type ExportColumns<T> =
  | Array<React.ReactElement<PillarTableExportToExcelColumnProps<T>>>
  | React.ReactElement<PillarTableExportToExcelColumnProps<T>>;

/** One sheet of an export: an XLSX tab, or one CSV file. */
export type ExportSheet = {
  /** Sheet name; also the end of the CSV file name for nested tables */
  name: string;
  /** Header row followed by one row per record */
  data: unknown[][];
};

/** Everything one export writes. */
export type ExportSheets = {
  /** The table's own rows */
  mainSheet: ExportSheet;
  /** One sheet per nested table that has rows, each row prefixed with its parent row */
  nestedSheets: ExportSheet[];
};

type BuildExportSheetsProps<T> = {
  /** Every row to export */
  rows: T[];
  /** The table's columns, including nested tables */
  columns: ExportColumns<T>;
  /** Name of the main sheet, and the base for nested sheet names */
  worksheetName: string;
  /** Table context the column components read while rendering */
  tableContext: PillarTableContextType<T, unknown>;
  /** Session the column components read while rendering (society settings, admin mode) */
  session: CustomSessionType;
  /** Converts a rendered cell's HTML to its text */
  htmlToText: ExportHtmlToText;
};

/**
 * Renders a table's columns for every row and returns the export's sheets.
 * Shared by the browser Export button and the server report downloads, so
 * both produce the same files.
 */
export const buildExportSheets = <T,>({
  rows,
  columns,
  worksheetName,
  tableContext,
  session,
  htmlToText,
}: BuildExportSheetsProps<T>): ExportSheets => {
  // Extract headers from children, handling both new and legacy patterns
  const childArray = React.Children.toArray(columns);

  const mainTable = childArray.filter(
    (el): el is React.ReactElement =>
      React.isValidElement(el) && !isPillarTableNestedTable(el),
  );
  const filteredMainTable = mainTable.filter(filterTable);

  const mainTableHeaders = filteredMainTable.map((column) =>
    resolveExportHeading({ column, rows }),
  );
  const mainTableData = processRows<T>({
    rows,
    columns: filteredMainTable,
    tableContext,
    session,
    htmlToText,
  });

  const nestedTables = childArray.filter(isPillarTableNestedTable);

  // Nested tables exported as columns widen the main sheet rather than getting
  // a sheet of their own, e.g. one Score/Comment pair per reviewer.
  const pivotedNestedTables = nestedTables
    .filter((nestedTable) => nestedTable.props.exportLayout?.as === "columns")
    .map((nestedTable) => {
      const nestedTableColumns = resolveNestedTableColumns({
        nestedTable,
        adminMode: session?.adminMode,
      });
      const { groupByPath, groupHeading } =
        nestedTable.props.exportLayout ?? {};

      return pivotPillarTableNestedRows({
        nestedRowsByParent: rows.map((row) =>
          getPillarTableNestedRows(row, nestedTable.props.valuePath),
        ),
        groupByPath,
        groupHeading,
        columnHeadings: nestedTableColumns.map(retrieveHeaders),
        renderNestedRow: (nestedRow) =>
          processRows({
            rows: [nestedRow],
            columns: nestedTableColumns,
            tableContext,
            session,
            htmlToText,
          })[0] ?? [],
      });
    });

  const mainSheetData = [
    [
      ...mainTableHeaders,
      ...pivotedNestedTables.flatMap(({ headings }) => headings),
    ],
    ...mainTableData.map((row, rowIndex) => [
      ...row,
      ...pivotedNestedTables.flatMap(({ rows }) => rows[rowIndex] ?? []),
    ]),
  ];

  const sheetNestedTables = nestedTables.filter(
    ({ props: { exportLayout } }) =>
      exportLayout?.as !== "columns" || exportLayout.includeRowsSheet,
  );
  const nestedWorksheetNames = buildNestedWorksheetNames({
    nestedTables: sheetNestedTables,
    worksheetName,
  });

  const nestedSheets = sheetNestedTables.map(
    (nestedTable, nestedTableIndex): ExportSheet => {
      const nestedTableColumns = resolveNestedTableColumns({
        nestedTable,
        adminMode: session?.adminMode,
      });

      // Every parent column, not just the first one: a nested row on its own
      // sheet is unreadable without the whole parent row it belongs to.
      const nestedTableHeaders = [
        ...mainTableHeaders,
        ...nestedTableColumns.map(retrieveHeaders),
      ];

      const nestedTablePath = nestedTable.props.valuePath;

      const linkedNestedRows = rows.flatMap((row, rowIndex) => {
        const rowNestedItems = lodashGet(row, nestedTablePath) || [];
        const parentRow = mainTableData[rowIndex] ?? [];
        return rowNestedItems.map((nestedRow: T) => ({
          parentRow,
          data: nestedRow,
        }));
      });

      const nestedDataOnly = linkedNestedRows.map((row) => row.data);
      const processedNestedData = processRows({
        rows: nestedDataOnly,
        columns: nestedTableColumns,
        tableContext,
        session,
        htmlToText,
      });

      const nestedTableData = processedNestedData.map((row, index) => [
        ...(linkedNestedRows[index]?.parentRow ?? []),
        ...row,
      ]);

      return {
        name: nestedWorksheetNames[nestedTableIndex],
        data: nestedTableData.length
          ? [nestedTableHeaders, ...nestedTableData]
          : [],
      };
    },
  );

  return {
    mainSheet: {
      name: worksheetName,
      data: mainSheetData,
    },
    nestedSheets: nestedSheets.filter((sheet) => sheet.data.length),
  };
};

/** Turns a custom report's export column configs into column elements. */
export const buildExportColumnsFromCustomReport = (
  exportColumns?: CustomReportColumnConfig[],
): React.ReactElement[] => {
  if (!exportColumns?.length) {
    return [];
  }

  return exportColumns
    .map(
      (
        customReportColumnConfig: CustomReportColumnConfig,
        columnIndex: number,
      ) =>
        renderCustomReportColumn(
          customReportColumnConfig,
          `export-column-${columnIndex}`,
        ),
    )
    .filter(
      (column): column is React.ReactElement =>
        column !== null && React.isValidElement(column),
    );
};

/** Excel rejects sheet names longer than 31 characters. */
export const truncateWorksheetName = (worksheetName: string): string =>
  worksheetName.length > 31 ? worksheetName.substring(0, 31) : worksheetName;

type RenderElementWithMockContextProps<T> = {
  /** The cell to render */
  element: React.ReactElement;
  /** The row the cell belongs to */
  rowValue: T;
  /** Table context provided to the cell */
  tableContext: PillarTableContextType<T, unknown>;
  /** Session provided to the cell */
  session: CustomSessionType;
  /** Converts the rendered HTML to text */
  htmlToText: ExportHtmlToText;
};

// Helper function to render React elements with mock row context for export
const renderElementWithMockContext = <T,>({
  element,
  rowValue,
  tableContext,
  session,
  htmlToText,
}: RenderElementWithMockContextProps<T>): string => {
  // Create a mock row context that satisfies the PillarTableRowContext requirements
  const mockRowContext = {
    rowValue,
    setRowValue: () => {
      // No-op for export
    },
    rowIndex: 0,
    rowExpanded: "none" as const,
    setRowExpanded: () => {
      // No-op for export
    },
    inlineEditMode: "none" as const,
    formValues: {},
    setFormValues: () => {
      // No-op for export
    },
    setInlineEditMode: () => {
      // No-op for export
    },
    setRowActionFailure: () => {
      // No-op for export
    },
  };

  // Wrap the element with the row context
  const elementWithContext = (
    <CustomSessionContext.Provider value={session}>
      <PillarTableContext.Provider
        value={
          tableContext as PillarTableContextType<unknown, unknown, unknown>
        }
      >
        <PillarTableRowContext.Provider value={mockRowContext}>
          {element}
        </PillarTableRowContext.Provider>
      </PillarTableContext.Provider>
    </CustomSessionContext.Provider>
  );

  try {
    // Use renderToStaticMarkup to render the React element to HTML string
    const htmlString = renderToStaticMarkup(elementWithContext);
    if (isPlainExportText(htmlString)) {
      return htmlString.trim();
    }

    // Extract text content from HTML by removing all HTML tags
    const textContent = htmlToText(htmlString);

    return `${textContent || ""}`;
  } catch (error) {
    // console.warn("Failed to render element for export:", error);
    return "";
  }
};

type ProcessRowsProps<T> = {
  /** Rows to render */
  rows: T[];
  /** Columns to render for each row */
  columns: React.ReactElement[];
  /** Table context provided to each cell */
  tableContext: PillarTableContextType<T, unknown>;
  /** Session provided to each cell */
  session: CustomSessionType;
  /** Converts a rendered cell's HTML to text */
  htmlToText: ExportHtmlToText;
};

const processRows = <T,>({
  rows,
  columns,
  tableContext,
  session,
  htmlToText,
}: ProcessRowsProps<T>): unknown[][] => {
  const outputData: unknown[][] = [];

  rows.forEach((rowValue) => {
    const rowOutput: unknown[] = [];
    try {
      columns.forEach((child) => {
        if (!React.isValidElement<unknown>(child)) {
          // Safely skip anything that isn't a valid React element
          return;
        }

        const props = child.props as Record<string, unknown>;
        const actualColumnType = findPillarTableColumnComponent(child.type);
        //Legacy compatibility for ExportToExcelColumn even though it is deprecated
        if (
          !(
            (actualColumnType &&
              (!("doNotExport" in actualColumnType) ||
                !actualColumnType.doNotExport)) ||
            child.type === PillarTableExportToExcelColumn
          )
        ) {
          return;
        }

        if (
          actualColumnType &&
          "adminOnly" in actualColumnType &&
          actualColumnType.adminOnly
        ) {
          return;
        }

        let output: unknown;

        //TODO:  This mess is necessary for backwards compatibility with the old ExportToExcelColumn pattern
        // Handle the pattern similar to PillarTableCellContent
        if ("children" in props === false) {
          // No children prop - render the React element with mock context
          output = renderElementWithMockContext<T>({
            element: child,
            rowValue,
            tableContext,
            session,
            htmlToText,
          });
        } else if (typeof props.children === "function") {
          const cellValue =
            props.valueProperty || props.cellValuePath
              ? lodashGet(
                  rowValue,
                  (props.valueProperty ?? props.cellValuePath) as string,
                )
              : rowValue;
          // Children is a function - call it with the row value and create element
          const functionResult = (props.children as (item: T) => unknown)(
            cellValue,
          );
          // Create a React element with the function result and pass to renderElementWithMockContext
          const elementWithResult = React.createElement(
            "span",
            {},
            functionResult as React.ReactNode,
          );
          output = renderElementWithMockContext<T>({
            element: elementWithResult,
            rowValue,
            tableContext,
            session,
            htmlToText,
          });
        } else {
          output = renderElementWithMockContext<T>({
            element: child,
            rowValue,
            tableContext,
            session,
            htmlToText,
          });
        }

        // Ensure output is a string or number for Excel
        if (React.isValidElement(output)) {
          // If output is a React element, try to extract text content
          output = extractTextFromReactElement(output);
        } else if (typeof output === "string" || typeof output === "number") {
          //Don't do anything if it's already a string or number. Should only happen from Legacy ExportToExcelColumn functions
        } else if (output === null || output === undefined) {
          output = "";
        }

        rowOutput.push(output);
      });
    } catch (e) {
      console.error(e);
    }
    outputData.push(rowOutput);
  });
  return outputData;
};

// Helper function to extract text content from React elements
const extractTextFromReactElement = (element: React.ReactElement): string => {
  if (typeof element === "string" || typeof element === "number") {
    return String(element);
  }

  if (React.isValidElement(element)) {
    const props = element.props as Record<string, unknown>;
    const children = props?.children;
    if (typeof children === "string" || typeof children === "number") {
      return String(children);
    }
    if (Array.isArray(children)) {
      return children
        .map((child) =>
          React.isValidElement(child)
            ? extractTextFromReactElement(child)
            : String(child),
        )
        .join("");
    }
    if (React.isValidElement(children)) {
      return extractTextFromReactElement(children);
    }
  }

  return "";
};

const filterTable = (child: unknown): boolean => {
  if (React.isValidElement(child)) {
    try {
      const actualColumnType = findPillarTableColumnComponent(child.type);
      const shouldExclude =
        actualColumnType &&
        "doNotExport" in actualColumnType &&
        actualColumnType.doNotExport;
      if (shouldExclude) {
        return false;
      }
      // If it's a PillarTableExportToExcelColumn, we want to include it even though it is deprecated for legacy reasons
      if (!actualColumnType && child.type !== PillarTableExportToExcelColumn) {
        return false;
      }

      return true;
    } catch (error) {
      // If we can't determine the column type, include it by default
      return false;
    }
  }
  return true;
};

const retrieveHeaders = (child: unknown): string => {
  if (React.isValidElement(child)) {
    const props = child.props as unknown as Record<string, unknown>;
    // Handle new PillarTable.Column pattern with heading
    if (props.heading) {
      return props.heading as string;
    }
    // Handle legacy ExportToExcelColumn pattern with header
    if (props.header) {
      return props.header as string;
    }
  }
  return "Unknown";
};

type ResolveExportHeadingProps<T> = {
  /** The column whose heading is exported */
  column: React.ReactElement;
  /** Every exported row, used to fill an ArrayFind heading template */
  rows: T[];
};

/**
 * The heading a column exports under. An ArrayFind heading can be a template
 * naming the element it finds, which takes the rows to work out.
 */
const resolveExportHeading = <T,>({
  column,
  rows,
}: ResolveExportHeadingProps<T>): string => {
  const { heading, cellValuePath, findPath, findValue } = column.props as {
    heading?: string;
    cellValuePath?: string;
    findPath?: string;
    findValue: string | number | string[] | number[];
  };
  if (
    column.type !== PillarTableColumnArrayFind ||
    !isPillarTableHeadingTemplate(heading)
  ) {
    return retrieveHeaders(column);
  }
  return resolvePillarTableArrayFindHeading({
    heading,
    rows,
    cellValuePath,
    findPath,
    findValue,
  });
};

type ResolveNestedTableColumnsProps = {
  /** The nested table element */
  nestedTable: PillarTableNestedTableElement;
  /** Admin mode keeps `adminOnly` column configs */
  adminMode?: boolean;
};

/**
 * The columns one nested table exports: its custom report config when it came
 * from the report builder, otherwise the columns written inside its JSX.
 */
const resolveNestedTableColumns = ({
  nestedTable,
  adminMode,
}: ResolveNestedTableColumnsProps): React.ReactElement[] => {
  const { nestedTableColumns, children } = nestedTable.props as {
    nestedTableColumns?: CustomReportColumnConfig[];
    children?: React.ReactNode;
  };

  if (nestedTableColumns?.length) {
    return nestedTableColumns
      .filter((columnConfig) => adminMode || !columnConfig.adminOnly)
      .map((columnConfig, columnIndex) =>
        renderCustomReportColumn(
          columnConfig,
          `nested-export-column-${columnIndex}`,
        ),
      )
      .filter((column): column is React.ReactElement => column !== null);
  }

  return React.Children.toArray(children)
    .filter((child): child is React.ReactElement => React.isValidElement(child))
    .filter(filterTable);
};

type BuildNestedWorksheetNamesProps = {
  /** The nested tables, in column order */
  nestedTables: PillarTableNestedTableElement[];
  /** Main sheet name, which nested names must not repeat */
  worksheetName: string;
};

/**
 * Sheet names for the nested tables, unique within the workbook. A nested
 * table's `heading` names its sheet when it has one, because "Nested
 * information 3" tells the reader nothing once a row has several relations.
 */
const buildNestedWorksheetNames = ({
  nestedTables,
  worksheetName,
}: BuildNestedWorksheetNamesProps): string[] => {
  const usedNames = new Set<string>([truncateWorksheetName(worksheetName)]);

  return nestedTables.map((nestedTable, nestedTableIndex) => {
    const baseName =
      (nestedTable.props as { heading?: string }).heading ||
      (nestedTableIndex === 0
        ? "Nested information"
        : `Nested information ${nestedTableIndex + 1}`);

    let uniqueName = truncateWorksheetName(baseName);
    let duplicateCount = 2;
    while (usedNames.has(uniqueName)) {
      uniqueName = truncateWorksheetName(`${baseName} ${duplicateCount}`);
      duplicateCount++;
    }
    usedNames.add(uniqueName);
    return uniqueName;
  });
};
